import React, { useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ShortcutIcon from '@mui/icons-material/Keyboard';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PrintIcon from '@mui/icons-material/Print';
import FlagIcon from '@mui/icons-material/Flag';
import { useHelp } from '../../context/System/HelpContext';

const drawerWidth = 380;

const SizeSoftXHelpPanel = () => {
    const { helpConfig, isOpen, isSpeaking, closeHelpPanel, speakHelp, stopSpeech } = useHelp();

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                e.preventDefault();
                closeHelpPanel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeHelpPanel]);

    useEffect(() => {
        if (!isOpen) {
            stopSpeech();
        }
    }, [isOpen, stopSpeech]);

    const handleClose = () => {
        closeHelpPanel();
    };

    return (
        <Drawer
            anchor="left"
            open={isOpen}
            onClose={handleClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: { xs: '100%', sm: drawerWidth },
                    maxWidth: '100vw',
                    backgroundColor: '#fafafa',
                },
            }}
            PaperProps={{
                sx: {
                    boxShadow: '8px 0 32px rgba(0,0,0,0.12)',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                        color: '#fff',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <FlagIcon sx={{ fontSize: 22 }} />
                        {helpConfig?.title || 'Ayuda'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 1.5, pb: 1, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5} justifyContent="center">
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Comenzar"
                            onClick={() => speakHelp('start')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Trabajar"
                            onClick={() => speakHelp('work')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Terminar"
                            onClick={() => speakHelp('end')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Imprimir"
                            onClick={() => speakHelp('print')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Atajos"
                            onClick={() => speakHelp('shortcuts')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            icon={<PlayArrowIcon />}
                            label="Consejos"
                            onClick={() => speakHelp('tips')}
                            clickable
                            size="small"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            label="Resumen"
                            onClick={() => speakHelp('all')}
                            clickable
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                        />
                    </Stack>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                    {helpConfig?.description && (
                        <Box sx={{ p: 2, pb: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#424242',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    backgroundColor: '#e3f2fd',
                                    p: 1.5,
                                    borderRadius: 1,
                                    borderLeft: '3px solid #1976d2',
                                }}
                            >
                                {helpConfig.description}
                            </Typography>
                        </Box>
                    )}

                    {helpConfig?.howToStart && (
                        <Accordion
                            defaultExpanded
                            sx={{
                                mx: 2, mb: 1, '&:before': { display: 'none' },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                borderRadius: '8px !important', overflow: 'hidden',
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e8f5e9', minHeight: 44 }}>
                                <Typography sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FlagIcon sx={{ fontSize: 18 }} />Como COMENZAR
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#424242', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                    {helpConfig.howToStart}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {helpConfig?.howToWork && (
                        <Accordion
                            defaultExpanded
                            sx={{
                                mx: 2, mb: 1, '&:before': { display: 'none' },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                borderRadius: '8px !important', overflow: 'hidden',
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff3e0', minHeight: 44 }}>
                                <Typography sx={{ fontWeight: 600, color: '#e65100', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ShortcutIcon sx={{ fontSize: 18 }} />Como TRABAJAR
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#424242', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                    {helpConfig.howToWork}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {helpConfig?.howToEnd && (
                        <Accordion
                            defaultExpanded
                            sx={{
                                mx: 2, mb: 1, '&:before': { display: 'none' },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                borderRadius: '8px !important', overflow: 'hidden',
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fce4ec', minHeight: 44 }}>
                                <Typography sx={{ fontWeight: 600, color: '#c2185b', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FlagIcon sx={{ fontSize: 18 }} />Como TERMINAR
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#424242', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                    {helpConfig.howToEnd}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {helpConfig?.howToPrint && (
                        <Accordion
                            sx={{
                                mx: 2, mb: 1, '&:before': { display: 'none' },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                borderRadius: '8px !important', overflow: 'hidden',
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e1f5fe', minHeight: 44 }}>
                                <Typography sx={{ fontWeight: 600, color: '#0277bd', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PrintIcon sx={{ fontSize: 18 }} />Como IMPRIMIR
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#424242', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                    {helpConfig.howToPrint}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {helpConfig?.shortcutKeys?.length > 0 && (
                        <Box sx={{ mx: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '0.9rem', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ShortcutIcon sx={{ fontSize: 18 }} />Atajos de Teclado
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                                {helpConfig.shortcutKeys.map((shortcut, index) => (
                                    <Chip
                                        key={index}
                                        label={`${shortcut.key}: ${shortcut.action}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#263238', color: '#fff', fontWeight: 600, fontSize: '0.75rem',
                                            fontFamily: 'monospace', height: 28, '& .MuiChip-label': { px: 1 },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {helpConfig?.steps?.length > 0 && (
                        <Box sx={{ px: 2, pb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '0.9rem', mb: 1 }}>
                                Pasos Disponibles
                            </Typography>
                            <List dense disablePadding>
                                {helpConfig.steps.map((step, index) => (
                                    <Accordion
                                        key={index}
                                        sx={{
                                            '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            mb: 0.5, borderRadius: '6px !important', overflow: 'hidden',
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f5f5f5', minHeight: 36 }}>
                                            <Chip label={step.key} size="small" sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20, minWidth: 20, mr: 1 }} />
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#212121' }}>{step.title}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.8rem', lineHeight: 1.6 }}>{step.content}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </List>
                        </Box>
                    )}

                    {helpConfig?.tips?.length > 0 && (
                        <Box sx={{ px: 2, pb: 2 }}>
                            <Divider sx={{ mb: 1.5 }}><LightbulbIcon sx={{ color: '#ff9800', fontSize: 20 }} /></Divider>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100', fontSize: '0.9rem', mb: 1 }}>Consejos Utiles</Typography>
                            <List dense disablePadding>
                                {helpConfig.tips.map((tip, index) => (
                                    <ListItem key={index} sx={{ py: 0.5, px: 1, backgroundColor: '#fff8e1', borderRadius: 1, mb: 0.5, borderLeft: '3px solid #ff9800' }}>
                                        <ListItemText primary={tip} primaryTypographyProps={{ sx: { fontSize: '0.8rem', color: '#5d4037' } }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {!helpConfig?.title?.includes('no disponible') && (
                        <Box sx={{ p: 2, pt: 0, position: 'sticky', bottom: 0, backgroundColor: '#fafafa' }}>
                            {isSpeaking && (
                                <Stack direction="row" spacing={1} justifyContent="center">
                                    <Chip
                                        icon={<StopIcon />}
                                        label="Detener"
                                        onClick={stopSpeech}
                                        color="error"
                                        clickable
                                        sx={{ fontWeight: 600 }}
                                    />
                                </Stack>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default SizeSoftXHelpPanel;
